import React, { useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import Button from "components/ui/Button";
import SearchSelect from "components/ui/SearchSelect";
import { Field, toOptions, LEVEL_HINT, ROLE_HINT } from "./RoleGrantEdit";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Same three questions as RoleGrantEdit (role, level, scope) — the difference
// is who it's for: an email, not a person picked from the list, because this
// person may not have a User yet. The backend finds them by email, creates
// one if needed, grants it, and emails a web login link.
export default function RoleGrantInvite({ options, onSubmit, isSubmitting }) {
  const {
    control,
    watch,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      email: "",
      name: "",
      role: "viewer",
      scopeLevel: "network",
      scopeId: "",
      primary: false,
    },
  });

  const scopeLevel = watch("scopeLevel");
  const role = watch("role");

  const levelOptions = useMemo(
    () =>
      (options?.scopeLevels || []).map((level) => ({
        value: level,
        label: level.charAt(0).toUpperCase() + level.slice(1),
      })),
    [options],
  );

  const roleOptions = useMemo(
    () =>
      (options?.roles || []).map((value) => ({
        value,
        label: value.charAt(0).toUpperCase() + value.slice(1),
      })),
    [options],
  );

  const scopeOptions = useMemo(
    () => toOptions(options?.scopes?.[scopeLevel]),
    [options, scopeLevel],
  );

  const submit = (values) =>
    onSubmit({
      ...values,
      email: values.email.trim().toLowerCase(),
      name: values.name.trim() || undefined,
      primary: values.role === "owner" ? values.primary : false,
    });

  return (
    <form onSubmit={handleSubmit(submit)} className="space-y-5">
      <Controller
        name="email"
        control={control}
        rules={{
          required: "An email is required",
          pattern: { value: EMAIL_PATTERN, message: "Enter a valid email address" },
        }}
        render={({ field }) => (
          <Field label="Email" error={errors.email?.message}>
            <input
              type="email"
              className="form-input border border-mediumgray"
              placeholder="name@example.com"
              disabled={isSubmitting}
              {...field}
            />
          </Field>
        )}
      />

      <Controller
        name="name"
        control={control}
        render={({ field }) => (
          <Field label="Name" hint="Optional — shown in the grants list once they've logged in.">
            <input
              type="text"
              className="form-input border border-mediumgray"
              placeholder="Their name"
              disabled={isSubmitting}
              {...field}
            />
          </Field>
        )}
      />

      <Controller
        name="role"
        control={control}
        rules={{ required: "Choose a role" }}
        render={({ field }) => (
          <Field label="Role" error={errors.role?.message} hint={ROLE_HINT[field.value]}>
            <SearchSelect
              options={roleOptions}
              value={roleOptions.find((option) => option.value === field.value) || null}
              onChange={(option) => {
                field.onChange(option?.value || "");
                if (option?.value !== "owner") setValue("primary", false);
              }}
              placeholder="Select role..."
              searchable={false}
              disabled={isSubmitting}
            />
          </Field>
        )}
      />

      <Controller
        name="scopeLevel"
        control={control}
        rules={{ required: "Choose a level" }}
        render={({ field }) => (
          <Field label="Level" error={errors.scopeLevel?.message} hint={LEVEL_HINT[field.value]}>
            <SearchSelect
              options={levelOptions}
              value={levelOptions.find((option) => option.value === field.value) || null}
              onChange={(option) => {
                field.onChange(option?.value || "");
                setValue("scopeId", "");
              }}
              placeholder="Select level..."
              searchable={false}
              disabled={isSubmitting}
            />
          </Field>
        )}
      />

      <Controller
        name="scopeId"
        control={control}
        rules={{ required: `Choose which ${scopeLevel}` }}
        render={({ field }) => (
          <Field
            label="Scope"
            error={errors.scopeId?.message}
            hint={scopeOptions.length ? undefined : `No ${scopeLevel}s exist yet — create one first.`}
          >
            <SearchSelect
              options={scopeOptions}
              value={scopeOptions.find((option) => option.value === field.value) || null}
              onChange={(option) => field.onChange(option?.value || "")}
              placeholder={`Select ${scopeLevel}...`}
              disabled={isSubmitting || !scopeOptions.length}
            />
          </Field>
        )}
      />

      {role === "owner" ? (
        <Controller
          name="primary"
          control={control}
          render={({ field }) => (
            <Field
              label="Primary owner"
              hint="The invoiced contact. A scope can have many owners but only one primary — setting this moves it off whoever holds it now."
            >
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={Boolean(field.value)}
                  onChange={(event) => field.onChange(event.target.checked)}
                  disabled={isSubmitting}
                />
                Make this the primary owner
              </label>
            </Field>
          )}
        />
      ) : null}

      <div className="flex items-center justify-end gap-3 border-t border-mediumgray pt-4">
        <Button type="submit" className="black-button" loading={isSubmitting} disabled={isSubmitting}>
          Send Invite
        </Button>
      </div>
    </form>
  );
}
